import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Tag, RefreshCw, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { createResaleListing, cancelResaleListing } from "@/lib/resale-v2.functions";
import { formatPrice, formatDate } from "@/lib/mock-data";
import { TenantThemeProvider, PoweredByZyntra } from "@/components/TenantThemeProvider";
import { getBrandingForEvents, type TenantBranding } from "@/lib/branding.functions";
import {
  usePlatformFeeConfig,
  computeMaxResalePrice,
  computeResaleBreakdown,
} from "@/lib/fees";
import type { Tables } from "@/integrations/supabase/types";

type TicketWithEvent = Tables<"tickets"> & {
  events: Tables<"events"> | null;
  ticket_types: Tables<"ticket_types"> | null;
};

export const Route = createFileRoute("/my-tickets")({
  component: MyTickets,
  head: () => ({
    meta: [
      { title: "Mis Entradas — ZYNTRA" },
      { name: "description", content: "Consultá tus entradas digitales con QR dinámico." },
    ],
  }),
});

function MyTickets() {
  const { user, loading: authLoading } = useAuth();
  const [tickets, setTickets] = useState<TicketWithEvent[]>([]);
  const [brandingByEvent, setBrandingByEvent] = useState<Map<string, TenantBranding>>(new Map());
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<TicketWithEvent | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    loadTickets();
  }, [user, authLoading]);

  async function loadTickets() {
    setLoading(true);
    const { data } = await supabase
      .from("tickets")
      .select("*, events(*), ticket_types(*)")
      .eq("user_id", user!.id)
      .order("purchased_at", { ascending: false });

    const list = (data as TicketWithEvent[]) || [];
    setTickets(list);
    const eventIds = list.map((t) => t.event_id).filter(Boolean) as string[];
    if (eventIds.length > 0) {
      try {
        const map = await getBrandingForEvents(eventIds);
        setBrandingByEvent(map);
      } catch (e) {
        console.error("[my-tickets] branding load failed", e);
      }
    }
    setLoading(false);
  }

  if (!authLoading && !user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground">Iniciá sesión</h2>
          <p className="mt-2 text-muted-foreground">Necesitás una cuenta para ver tus entradas.</p>
          <Link to="/login" className="mt-4 inline-block rounded-xl gradient-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
            Ingresar
          </Link>
        </div>
      </div>
    );
  }

  if (selectedTicket) {
    const branding = brandingByEvent.get(selectedTicket.event_id) ?? null;
    return (
      <TenantThemeProvider branding={branding}>
        <TicketView
          ticket={selectedTicket}
          branding={branding}
          onBack={() => { setSelectedTicket(null); loadTickets(); }}
          userName={user?.user_metadata?.full_name || user?.email || ""}
          onUpdate={() => { loadTickets(); setSelectedTicket(null); }}
        />
      </TenantThemeProvider>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 pb-20 pt-6 sm:px-6">
      <h1 className="text-2xl font-black text-foreground sm:text-3xl">Mis Entradas</h1>
      <p className="mt-1 text-sm text-muted-foreground">Tocá una entrada para ver tu QR dinámico</p>

      {loading ? (
        <div className="mt-8 space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse rounded-2xl border border-border bg-card p-4">
              <div className="h-4 w-2/3 bg-muted rounded" />
              <div className="h-3 w-1/2 bg-muted rounded mt-2" />
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {tickets.map((ticket) => {
            const branding = brandingByEvent.get(ticket.event_id) ?? null;
            const isCustomTenant = !!branding && branding.tenant_slug !== "zyntra";
            // Each card scopes its own tenant theme so cards from multiple
            // tenants can coexist without polluting each other.
            return (
              <TenantThemeProvider key={ticket.id} branding={branding}>
                <button
                  onClick={() => setSelectedTicket(ticket)}
                  className="w-full text-left rounded-2xl border border-border bg-card p-4 transition-all card-hover hover:border-primary/40"
                  style={
                    isCustomTenant && branding?.primary_color
                      ? { borderLeft: `3px solid ${branding.primary_color}` }
                      : undefined
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {isCustomTenant && branding?.logo_url ? (
                          <img
                            src={branding.logo_url}
                            alt={branding.brand_name}
                            className="h-5 w-5 rounded object-contain bg-background"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : null}
                        <h3 className="text-sm font-bold text-card-foreground truncate">
                          {ticket.events?.title || "Evento"}
                        </h3>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {ticket.events ? `${formatDate(ticket.events.date)} · ${ticket.events.location}, ${ticket.events.city}` : ""}
                      </p>
                      <p className="text-xs text-primary font-medium mt-1">
                        {ticket.ticket_label || ticket.ticket_types?.name || ""} · {formatPrice((Number(ticket.subtotal ?? ticket.price) || 0) + (Number(ticket.fee_amount) || 0))}
                      </p>
                      {isCustomTenant && branding?.brand_name ? (
                        <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                          {branding.brand_name}
                        </p>
                      ) : null}
                    </div>
                    <StatusBadge status={ticket.status} />
                  </div>
                </button>
              </TenantThemeProvider>
            );
          })}
        </div>
      )}

      {!loading && tickets.length === 0 && (
        <div className="mt-16 text-center">
          <p className="text-muted-foreground">No tenés entradas aún.</p>
          <Link to="/" className="mt-3 inline-block text-sm text-primary hover:underline">Explorar eventos</Link>
        </div>
      )}
    </div>
  );
}

function TicketView({ ticket, branding, onBack, userName, onUpdate }: { ticket: TicketWithEvent; branding: TenantBranding | null; onBack: () => void; userName: string; onUpdate: () => void }) {
  const [qrTimestamp, setQrTimestamp] = useState(Date.now());
  const [showResaleForm, setShowResaleForm] = useState(false);
  const [resalePrice, setResalePrice] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState({ text: "", type: "" });

  useEffect(() => {
    const interval = setInterval(() => setQrTimestamp(Date.now()), 15000);
    return () => clearInterval(interval);
  }, []);

  const dynamicQr = `${ticket.qr_data}-${qrTimestamp}`;
  const feeCfg = usePlatformFeeConfig();
  const maxResalePrice = computeMaxResalePrice(ticket.price, feeCfg);
  // Histórico real: lo que efectivamente pagó el usuario al comprar.
  // NUNCA recalcular con la configuración actual de fees (puede haber cambiado).
  const totalPaidOriginal = Math.round(
    (Number(ticket.subtotal ?? ticket.price) || 0) + (Number(ticket.fee_amount) || 0)
  );
  const previewPrice = parseFloat(resalePrice) || 0;
  const breakdown = computeResaleBreakdown(ticket.price, previewPrice, feeCfg);
  const previewPlatformFee = breakdown.platformFee;
  const previewPaymentFee = breakdown.paymentFee;
  const previewNet = breakdown.sellerNet;
  // Resultado estimado vs lo realmente pagado (histórico), no vs simulación actual.
  const previewProfit = breakdown.sellerNet - totalPaidOriginal;

  async function handleListResale() {
    const price = parseFloat(resalePrice);
    if (isNaN(price) || price <= 0) {
      setActionMsg({ text: "Ingresá un precio válido", type: "error" });
      return;
    }
    setActionLoading(true);
    setActionMsg({ text: "", type: "" });
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      await createResaleListing({
        data: { ticketId: ticket.id, listingPrice: price },
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      } as any);
      setActionMsg({ text: "Ticket publicado en reventa", type: "success" });
      onUpdate();
    } catch (err: any) {
      setActionMsg({ text: err.message || "Error", type: "error" });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCancelResale() {
    setActionLoading(true);
    setActionMsg({ text: "", type: "" });
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const { data: listing } = await supabase
        .from("resale_listings")
        .select("id")
        .eq("ticket_id", ticket.id)
        .eq("status", "active")
        .maybeSingle();
      if (!listing) throw new Error("No se encontró publicación activa");
      await cancelResaleListing({
        data: { listingId: listing.id },
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      } as any);
      setActionMsg({ text: "Reventa cancelada", type: "success" });
      onUpdate();
    } catch (err: any) {
      setActionMsg({ text: err.message || "Error", type: "error" });
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-sm px-4 pb-20 pt-6 sm:px-6">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Volver
      </button>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="gradient-primary p-5 text-center">
          <div className="flex items-center justify-center gap-2">
            {branding?.logo_url && branding.tenant_slug !== "zyntra" ? (
              <img
                src={branding.logo_url}
                alt={branding.brand_name}
                className="h-5 w-5 rounded object-contain bg-white/20"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : null}
            <p className="text-xs font-medium text-primary-foreground/80 uppercase tracking-wider">
              {(branding?.brand_name || "ZYNTRA")} · Ticket Digital
            </p>
          </div>
          <h2 className="mt-1 text-lg font-black text-primary-foreground">{ticket.events?.title}</h2>
          <p className="text-xs text-primary-foreground/80 mt-1">
            {ticket.ticket_label || ticket.ticket_types?.name}
          </p>
        </div>

        {ticket.status !== "resale" && (
          <div className="flex flex-col items-center p-6">
            <div className="rounded-xl bg-white p-4">
              <QRCodeSVG value={dynamicQr} size={180} level="H" />
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3 animate-spin" style={{ animationDuration: "3s" }} />
              QR dinámico · se actualiza cada 15s
            </div>
          </div>
        )}

        {ticket.status === "resale" && (() => {
          const salePrice = ticket.resale_price || ticket.price;
          const liveBreakdown = computeResaleBreakdown(ticket.price, salePrice, feeCfg);
          const platformFee = liveBreakdown.platformFee;
          const paymentFee = liveBreakdown.paymentFee;
          const netAmount = liveBreakdown.sellerNet;
          // Profit comparado contra el histórico real, no contra recálculo con config actual.
          const liveProfit = netAmount - totalPaidOriginal;
          return (
            <div className="flex flex-col items-center p-6">
              <Tag className="h-12 w-12 text-warning" />
              <p className="mt-3 text-sm font-bold text-warning">En reventa</p>
              <p className="text-lg font-black text-foreground mt-1">{formatPrice(salePrice)}</p>
              <div className="mt-4 w-full rounded-lg border border-border bg-secondary/40 p-3 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pagaste originalmente</span>
                  <span className="font-semibold text-foreground">{formatPrice(totalPaidOriginal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Precio de venta</span>
                  <span className="font-semibold text-foreground">{formatPrice(salePrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Comisión plataforma ({feeCfg.resale_platform_fee_percent}%)</span>
                  <span className="text-destructive">−{formatPrice(platformFee)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Comisión pago ({feeCfg.resale_payment_fee_percent}%)</span>
                  <span className="text-destructive">−{formatPrice(paymentFee)}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-1 mt-1">
                  <span className="font-bold text-foreground">Vas a recibir</span>
                  <span className="font-black gradient-text">{formatPrice(netAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Resultado estimado</span>
                  <span className={`font-bold ${liveProfit >= 0 ? "text-success" : "text-destructive"}`}>
                    {liveProfit >= 0 ? "+" : "−"}{formatPrice(Math.abs(liveProfit))}
                  </span>
                </div>
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground text-center">
                Este es el neto estimado después de comisiones. Se acredita a tu wallet cuando se concreta la venta.
              </p>
            </div>
          );
        })()}

        <div className="border-t border-dashed border-border px-5 py-4 space-y-2">
          <DetailRow label="Evento" value={ticket.events?.title || ""} />
          <DetailRow label="Fecha" value={ticket.events ? formatDate(ticket.events.date) : ""} />
          <DetailRow label="Ubicación" value={ticket.events ? `${ticket.events.location}, ${ticket.events.city}` : ""} />
          {ticket.ticket_label && (
            <DetailRow
              label={ticket.inventory_type === "seated" ? "Asiento" : "Zona"}
              value={<span className="font-bold text-primary">{ticket.ticket_label}</span>}
            />
          )}
          <DetailRow label="Titular" value={userName} />
          <DetailRow label="Pagaste" value={formatPrice(totalPaidOriginal)} />
          <DetailRow label="Estado" value={<StatusBadge status={ticket.status} />} />
          <DetailRow label="ID" value={<span className="font-mono text-xs">{ticket.id.slice(0, 8)}</span>} />
        </div>

        {actionMsg.text && (
          <div className={`mx-5 mb-3 rounded-lg px-3 py-2 text-xs font-medium ${
            actionMsg.type === "success" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
          }`}>
            {actionMsg.text}
          </div>
        )}

        {ticket.status === "active" && (ticket.is_free || ticket.is_resellable === false) && (
          <div className="border-t border-border p-5">
            <div className="rounded-xl border border-border bg-secondary/40 px-3 py-2.5 text-center text-xs text-muted-foreground">
              Esta entrada es gratuita y no puede ponerse en reventa.
            </div>
          </div>
        )}

        {ticket.status === "active" && !ticket.is_free && ticket.is_resellable !== false && (
          <div className="border-t border-border p-5">
            {!showResaleForm ? (
              <button
                onClick={() => setShowResaleForm(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-primary/30 bg-accent py-2.5 text-sm font-semibold text-accent-foreground transition-colors hover:bg-primary/10"
              >
                <Tag className="h-4 w-4" />
                Poner en reventa
              </button>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground">
                    Precio de venta (lo que paga el comprador, máx. {formatPrice(maxResalePrice)} · +{Math.round(feeCfg.max_resale_markup_percent - 100)}%)
                  </label>
                  <input
                    type="number"
                    value={resalePrice}
                    onChange={(e) => setResalePrice(e.target.value)}
                    placeholder={`Ej: ${ticket.price}`}
                    max={maxResalePrice}
                    className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Es el total final que verá el comprador en el marketplace, sin cargos extra. Pagaste originalmente <strong className="text-foreground">{formatPrice(totalPaidOriginal)}</strong>.
                  </p>
                </div>
                {previewPrice > 0 && (
                  <div className="rounded-lg border border-border bg-secondary/40 p-3 text-xs space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Pagaste</span><span className="font-semibold text-foreground">{formatPrice(totalPaidOriginal)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Precio venta</span><span className="font-semibold text-foreground">{formatPrice(previewPrice)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Comisión plataforma ({feeCfg.resale_platform_fee_percent}%)</span><span className="text-destructive">−{formatPrice(previewPlatformFee)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Comisión pago ({feeCfg.resale_payment_fee_percent}%{feeCfg.resale_payment_fee_fixed ? ` + ${formatPrice(feeCfg.resale_payment_fee_fixed)}` : ""})</span><span className="text-destructive">−{formatPrice(previewPaymentFee)}</span></div>
                    <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="font-bold text-foreground">Recibís neto</span><span className="font-black gradient-text">{formatPrice(previewNet)}</span></div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Resultado estimado</span>
                      <span className={`font-bold ${previewProfit >= 0 ? "text-success" : "text-destructive"}`}>
                        {previewProfit >= 0 ? "+" : "−"}{formatPrice(Math.abs(previewProfit))}
                      </span>
                    </div>
                    {previewProfit < 0 && (
                      <p className="mt-1 text-[10px] text-warning leading-snug">
                        Con el límite actual de reventa podrías recibir menos de lo que pagaste originalmente.
                      </p>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleListResale}
                    disabled={actionLoading}
                    className="flex-1 rounded-xl gradient-primary py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50"
                  >
                    {actionLoading ? "Publicando..." : "Publicar"}
                  </button>
                  <button
                    onClick={() => setShowResaleForm(false)}
                    aria-label="Cerrar"
                    className="rounded-xl border border-border bg-secondary px-4 py-2.5 text-sm font-medium text-secondary-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {ticket.status === "resale" && (
          <div className="border-t border-border p-5">
            <button
              onClick={handleCancelResale}
              disabled={actionLoading}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-destructive/30 bg-destructive/10 py-2.5 text-sm font-semibold text-destructive transition-colors hover:bg-destructive/20 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              {actionLoading ? "Cancelando..." : "Cancelar reventa"}
            </button>
          </div>
        )}
      </div>
      <PoweredByZyntra className="mt-4 text-center" />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-card-foreground font-medium">{typeof value === "string" ? value : value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: "bg-success/20 text-success",
    used: "bg-muted text-muted-foreground",
    resale: "bg-warning/20 text-warning",
    transferred: "bg-primary/20 text-primary",
    refunded: "bg-destructive/20 text-destructive",
  };
  const labels: Record<string, string> = {
    active: "Activo",
    used: "Usado",
    resale: "En reventa",
    transferred: "Transferido",
    refunded: "Reembolsada",
  };

  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${styles[status] || styles.active}`}>
      {labels[status] || status}
    </span>
  );
}
